import { createContext, useContext, useEffect, useState, Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "./api";

const ContentContext = createContext({ blocks: {}, loading: true, refresh: () => {} });

export function ContentProvider({ children }) {
  const [blocks, setBlocks] = useState({});
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const load = () => {
    // Cache-busting query param - defeats any ingress / CDN / browser cache
    // layer that ignores our `Cache-Control: no-store` header, so admin edits
    // are guaranteed to appear on the next refetch.
    return api
      .get(`/content?_=${Date.now()}`, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      })
      .then((r) => setBlocks(r.data || {}))
      .catch(() => {});
  };

  useEffect(() => {
    load().finally(() => setLoading(false));

    // Refetch the content map when the tab regains focus so edits made in the
    // admin tab show up immediately on the public site without a hard refresh.
    const onFocus = () => { load(); };
    const onVisibility = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Refetch on every SPA route change too - covers the "admin edits then
  // clicks a public-site link in the same tab" case where no focus/visibility
  // event ever fires.
  useEffect(() => {
    load();
  }, [location.pathname]);

  return (
    <ContentContext.Provider value={{ blocks, loading, refresh: load }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext);

// --- Inline markdown renderer ---
// Supports: **bold**, *italic*, [link text](https://url or /internal-path)
function splitBoldItalic(text, keyPrefix) {
  const out = [];
  let buf = "";
  let i = 0;
  let key = 0;
  const flush = () => {
    if (buf) {
      out.push(<Fragment key={`${keyPrefix}-${key++}`}>{buf}</Fragment>);
      buf = "";
    }
  };
  while (i < text.length) {
    if (text[i] === "*" && text[i + 1] === "*") {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        flush();
        out.push(<strong key={`${keyPrefix}-${key++}`}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === "*" && text[i + 1] !== "*") {
      const end = text.indexOf("*", i + 1);
      if (end !== -1 && end > i + 1) {
        flush();
        out.push(<em key={`${keyPrefix}-${key++}`}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  flush();
  return out;
}

export function renderInline(text) {
  if (!text) return null;
  const linkRe = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+|tel:[^\s)]+|\/[^\s)]*)\)/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push({ type: "text", value: text.slice(lastIdx, m.index), key: key++ });
    }
    parts.push({ type: "link", text: m[1], url: m[2], key: key++ });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    parts.push({ type: "text", value: text.slice(lastIdx), key: key++ });
  }

  return parts.flatMap((p) => {
    if (p.type === "link") {
      const linkClass = "font-medium underline underline-offset-[3px] decoration-[1.5px] text-[#0d9b80] hover:text-[#0a7e68] transition-colors";
      if (p.url.startsWith("/")) {
        return (
          <Link key={`lk-${p.key}`} to={p.url} className={linkClass}>
            {p.text}
          </Link>
        );
      }
      const isExternal = p.url.startsWith("http");
      return (
        <a
          key={`lk-${p.key}`}
          href={p.url}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className={linkClass}
        >
          {p.text}
        </a>
      );
    }
    return splitBoldItalic(p.value, `t-${p.key}`);
  });
}

// Component: renders editable content. Always provide a `default` prop matching the seed
// so the page reads fine before /api/content has loaded (or if the block is missing).
export function EditableText({ keyName, default: defaultValue = "", as: Tag = "span", className, ...rest }) {
  const { blocks } = useContent();
  const value = (blocks && blocks[keyName] != null && blocks[keyName] !== "") ? blocks[keyName] : defaultValue;
  return (
    <Tag className={className} data-content-key={keyName} {...rest}>
      {renderInline(value)}
    </Tag>
  );
}

// Helper hook to get raw text (no markup parsing) - useful for placeholders, alt text, attributes.
export function useContentText(keyName, defaultValue = "") {
  const { blocks } = useContent();
  return (blocks && blocks[keyName] != null && blocks[keyName] !== "") ? blocks[keyName] : defaultValue;
}
